import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  endorsementCatalog,
  policies,
  type EndorsementType,
} from '@/shared/api/resources';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';

import { caminhoDaSelecao } from '../aftercare/endosso';

import { numeroDaApolice, tomadorDaApolice, vigenciaDe } from './apolice';

/**
 * "Novo endosso" do cabecalho da lista de apolices (APO-023, END-014 a END-017).
 *
 * Primeiro o painel do OnPoint, "Selecione o tipo de endosso que deseja iniciar", com
 * os tipos habilitados da empresa e a descricao de cada um; sem tipo habilitado, o
 * aviso do OnPoint. Depois, a apolice pelo numero exato, e a tela de selecao do
 * subtipo (P-END-1).
 */
export function NewEndorsementDialog({
  open,
  onClose,
  tipoInicial,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * Tipo ja escolhido em outro painel, como a escolha "Selecione o tipo de cotacao que
   * deseja iniciar" da lista de cotacoes da corretora (P-NCT): o dialogo abre direto na
   * apolice.
   */
  tipoInicial?: EndorsementType | null | undefined;
}) {
  const navigate = useNavigate();
  const [tipo, setTipo] = useState<EndorsementType | null>(tipoInicial ?? null);
  const [numero, setNumero] = useState('');

  const tipos = useQuery({
    queryKey: ['endorsement-types', 'habilitados'],
    queryFn: ({ signal }) => endorsementCatalog.types(true, signal),
    enabled: open,
    retry: false,
  });
  const procurar = useMutation({
    mutationFn: (texto: string) => policies.list({ number: texto, limit: 5 }),
  });
  const achadas = procurar.data?.items ?? [];
  const habilitados = tipos.data ?? [];

  function fechar() {
    setTipo(null);
    setNumero('');
    procurar.reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={fechar}
      title="Novo endosso"
      description={
        tipo
          ? `${tipo.name}: informe o numero da apolice que vai receber o endosso.`
          : 'Selecione o tipo de endosso que deseja iniciar'
      }
      size="md"
      footer={
        <>
          {tipo ? (
            <Button
              variant="secondary"
              onClick={() => {
                setTipo(null);
                procurar.reset();
              }}
            >
              Voltar
            </Button>
          ) : null}
          <Button variant="secondary" onClick={fechar}>
            Cancelar
          </Button>
        </>
      }
    >
      {!tipo ? (
        <div className="flex flex-col gap-2">
          {tipos.isPending ? (
            <p className="text-sm text-ink-500">Carregando dados</p>
          ) : null}
          {tipos.isError ? (
            <Alert tone="danger" title="Erro ao carregar permissoes">
              {tipos.error.message}
            </Alert>
          ) : null}
          {tipos.isSuccess && habilitados.length === 0 ? (
            <Alert tone="warning" title="Atencao!">
              Desculpe, mas nao e possivel solicitar um endosso pois nao existe nenhum
              tipo habilitado.
            </Alert>
          ) : null}
          {habilitados.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTipo(item)}
              className={cn(
                'flex flex-col items-start gap-0.5 rounded-md border border-line px-4 py-3 text-left',
                'hover:border-brand-500 hover:bg-brand-50 focus-visible:outline-2 focus-visible:outline-brand-500',
              )}
            >
              <span className="text-sm font-medium text-ink-900">{item.name}</span>
              <span className="text-xs text-ink-600">{item.description}</span>
              {item.calculation_pending ? (
                <span className="text-2xs text-warning-700">
                  O calculo deste tipo ainda nao foi homologado: o rascunho fica
                  guardado.
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (numero.trim() !== '') procurar.mutate(numero.trim());
          }}
        >
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Field
                label="Numero da apolice"
                placeholder="Digite o no da apolice"
                value={numero}
                onChange={(event) => setNumero(event.target.value)}
              />
            </div>
            <Button type="submit" variant="secondary" loading={procurar.isPending}>
              Procurar
            </Button>
          </div>
          {procurar.isError ? (
            <Alert tone="danger" title="Erro ao buscar apolice">
              {procurar.error.message}
            </Alert>
          ) : null}
          {procurar.isSuccess && achadas.length === 0 ? (
            <p className="text-sm text-ink-600">Nenhuma apolice com este numero.</p>
          ) : null}
          <ul className="flex flex-col gap-2">
            {achadas.map((apolice) => (
              <li
                key={apolice.id}
                className="flex items-center justify-between gap-3 rounded-md border border-line px-3 py-2"
              >
                <div className="min-w-0 text-sm">
                  <p className="font-medium text-ink-900">
                    Apolice {numeroDaApolice(apolice)}
                  </p>
                  <p className="truncate text-2xs text-ink-500">
                    {tomadorDaApolice(apolice).nome} -{' '}
                    {formatMoney(apolice.insured_amount)} -{' '}
                    {vigenciaDe(apolice.coverage_start, apolice.coverage_end)}
                  </p>
                </div>
                <Button
                  size="sm"
                  disabled={apolice.status !== 'vigente'}
                  onClick={() => {
                    const destino = caminhoDaSelecao(apolice.id, tipo.code);
                    fechar();
                    void navigate(destino);
                  }}
                >
                  Continuar
                </Button>
              </li>
            ))}
          </ul>
        </form>
      )}
    </Modal>
  );
}
